// src/components/profile/JobHistory.jsx
import React, { useState } from 'react';
import { 
  FiStar, FiCalendar, FiDollarSign, FiUser, FiMessageSquare,
  FiFilter, FiChevronDown, FiChevronUp
} from 'react-icons/fi';

const JobHistory = ({ jobHistory, isOwnProfile }) => {
  const [filter, setFilter] = useState('all');
  const [expandedJob, setExpandedJob] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const filteredJobs = jobHistory.filter(job => {
    if (filter === 'all') return true;
    if (filter === 'completed') return job.status === 'completed';
    if (filter === 'rated') return job.rating > 0;
    return true;
  });

  const completedJobs = jobHistory.filter(job => job.status === 'completed');
  const ratedJobs = jobHistory.filter(job => job.rating > 0);
  const averageRating = ratedJobs.length > 0 
    ? (ratedJobs.reduce((sum, job) => sum + job.rating, 0) / ratedJobs.length).toFixed(1)
    : 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Work History & Reviews
        </h3>
        
        <div className="flex items-center space-x-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Jobs</option>
            <option value="completed">Completed</option>
            <option value="rated">With Reviews</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{completedJobs.length}</div>
          <div className="text-sm text-gray-600">Jobs Completed</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <span className="text-2xl font-bold text-gray-900 mr-2">{averageRating}</span>
            <FiStar className="text-yellow-400 fill-current" size={20} />
          </div>
          <div className="text-sm text-gray-600">Average Rating</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{ratedJobs.length}</div>
          <div className="text-sm text-gray-600">Reviews</div>
        </div>
      </div>

      {/* Job List */}
      {filteredJobs.length > 0 ? (
        <div className="space-y-4">
          {filteredJobs.map((job) => (
            <div key={job.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">{job.title}</h4>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <FiUser size={14} className="mr-1" />
                      {isOwnProfile ? job.clientName : 'Client'}
                    </div>
                    <div className="flex items-center">
                      <FiCalendar size={14} className="mr-1" />
                      {new Date(job.completedAt || job.createdAt).toLocaleDateString()}
                    </div>
                    <div className="flex items-center">
                      <FiDollarSign size={14} className="mr-1" />
                      {formatCurrency(job.totalPaid || job.budget)}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                    {job.status}
                  </span>
                  
                  {job.rating > 0 && (
                    <div className="flex items-center space-x-1">
                      {renderStars(job.rating)}
                      <span className="text-sm font-medium text-gray-900 ml-1">
                        {job.rating}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {job.description && (
                <p className="text-gray-700 text-sm mb-3 line-clamp-2">
                  {job.description}
                </p>
              )}

              {/* Client Review */}
              {job.review && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <FiUser className="text-blue-600" size={16} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-blue-900">
                          {isOwnProfile ? job.clientName : 'Client'}
                        </span>
                        {renderStars(job.rating)}
                      </div>
                      <p className="text-blue-800 text-sm">{job.review}</p>
                      <p className="text-blue-600 text-xs mt-1">
                        {new Date(job.reviewDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* VA Response to Review */}
              {job.vaResponse && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                      <FiMessageSquare className="text-gray-600" size={16} />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 mb-1">Response</div>
                      <p className="text-gray-700 text-sm">{job.vaResponse}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Expandable Job Details */}
              {job.details && (
                <div>
                  <button
                    onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    View Details
                    {expandedJob === job.id ? (
                      <FiChevronUp className="ml-1" size={14} />
                    ) : (
                      <FiChevronDown className="ml-1" size={14} />
                    )}
                  </button>
                  
                  {expandedJob === job.id && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <h5 className="font-medium text-gray-900 mb-2">Project Details</h5>
                      <p className="text-gray-700 text-sm mb-3">{job.details}</p>
                      
                      {job.deliverables && (
                        <div>
                          <h6 className="font-medium text-gray-900 mb-1">Deliverables</h6>
                          <ul className="text-sm text-gray-700 list-disc list-inside">
                            {job.deliverables.map((deliverable, index) => (
                              <li key={index}>{deliverable}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <FiUser className="text-gray-400" size={24} />
          </div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            {filter === 'completed' ? 'No Completed Jobs' : 
             filter === 'rated' ? 'No Reviews Yet' : 'No Work History'}
          </h4>
          <p className="text-gray-600">
            {isOwnProfile 
              ? 'Complete jobs to build your reputation and showcase your work'
              : 'This VA hasn\'t completed any jobs yet'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default JobHistory;